import {
 GridColDef,
 GridFilterModel,
 GridInitialState,
 GridPaginationModel,
 GridRowParams,
 GridSortModel,
 MuiEvent,
} from '@mui/x-data-grid-premium';
import {
 MutableRefObject,
 useCallback,
 useEffect,
 useRef,
 useState,
} from 'react';

import {
 getConfigStorageItem,
 setConfigStorageItem,
} from '../../../../hooks/useConfigStorage';
import {
 changeCurrentPreset,
 ColPresetType,
 getClientConfigurationSelfStart,
 PageNameType,
} from '../../../../redux/clientConfiguration/clientConfigurationSlice';
import {
 areFilterFieldsEqual,
 deserializeFilters,
 filterFieldsToGridItems,
 hasActiveFilterFields,
 updateUrlFilterParams,
} from '../urlFilterParams';
import { buildMergedPreset } from '../filterModel';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { getBaseDomain } from '../../../../redux/pageDomain';
import { RootState } from '../../../../redux/store';
import { AUTOSIZE_DELAY_MS } from '../constants';
import { CustomGridApi } from '../types';
import {
 persistTableGridState,
 readStoredFilterModel,
 readStoredInitialState,
 readStoredPaginationModel,
 readStoredSortModel,
 writeStoredTableStateRaw,
} from '../utils';
import { useXTableContext } from '../XTableContext';

interface UseTableInitParams {
 pageName: PageNameType;
 apiRef: MutableRefObject<CustomGridApi>;
 table: string;
 defaultPageSize: number;
 columnsState: GridColDef[];
 presetsColumns: { [key: string]: ColPresetType[] } | null;
}

const useTableInit = ({
 pageName,
 apiRef,
 table,
 defaultPageSize,
 columnsState,
 presetsColumns,
}: UseTableInitParams) => {
 const { currentId, setCurrentId } = useXTableContext();
 const dispatch = useAppDispatch();

 const currentColumnsPreset = useAppSelector(
  (state: RootState) =>
   state.clientConfiguration.currentColumnsPreset?.[pageName] ?? null,
 );

 const success = useAppSelector((state: RootState) => {
  const pageState = state[getBaseDomain(pageName)];
  return pageState && 'success' in pageState ? pageState.success : false;
 });

 const loading = useAppSelector((state: RootState) => {
  const pageState = state[getBaseDomain(pageName)];
  return pageState && 'loading' in pageState
   ? !!(pageState.loading?.getTable || pageState.loading?.getTableStructure)
   : false;
 });

 const [isInit, setIsInit] = useState(false);
 const [filter, setFilter] = useState<GridFilterModel>(() =>
  readStoredFilterModel(pageName),
 );
 const [sortModel, setSortModel] = useState<GridSortModel>(() =>
  readStoredSortModel(pageName),
 );
 const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
  () => readStoredPaginationModel(pageName, { page: 0, pageSize: defaultPageSize }),
 );
 const [initialState, setInitialState] = useState<GridInitialState>(() =>
  readStoredInitialState(pageName),
 );

 const autosizeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
  undefined,
 );
 const isSelectingRef = useRef(false);
 const hadColumnsRef = useRef(false);

 useEffect(() => {
  return () => {
   if (autosizeTimerRef.current !== undefined) {
    clearTimeout(autosizeTimerRef.current);
   }
  };
 }, []);

 useEffect(() => {
  const tableSuccess = (success as Record<string, boolean>)?.getTable;
  if (!tableSuccess) return;

  if (autosizeTimerRef.current !== undefined) {
   clearTimeout(autosizeTimerRef.current);
  }
  autosizeTimerRef.current = setTimeout(() => {
   autosizeTimerRef.current = undefined;
   apiRef.current?.autosizeColumns?.({
    columns: columnsState
     .filter((col) => col.width === undefined)
     .map((col) => col.field),
    includeHeaders: false,
    includeOutliers: true,
   });
  }, AUTOSIZE_DELAY_MS);
 }, [(success as Record<string, boolean>)?.getTable, columnsState]);

 const saveSnapshot = useCallback(() => {
  if (apiRef?.current?.exportState && localStorage) {
   const currentState = apiRef.current.exportState();
   writeStoredTableStateRaw(pageName, currentState);
  }
 }, [apiRef, pageName]);

 useEffect(() => {
  const handleMouseDown = () => {
   isSelectingRef.current = false;
  };

  const handleMouseMove = () => {
   isSelectingRef.current = true;
  };

  const lsPreset = getConfigStorageItem(table)?.columns;
  if (lsPreset) {
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: lsPreset,
     temp: false,
    }),
   );
  } else if (presetsColumns?.[pageName]?.[0]) {
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: presetsColumns[pageName][0],
     temp: false,
    }),
   );
   setConfigStorageItem({
    page: pageName as PageNameType,
    key: 'columns',
    value: presetsColumns[pageName][0],
   });
  }

  if (!presetsColumns) {
  dispatch(getClientConfigurationSelfStart({ cached: true }));
  }

  const urlParams = new URLSearchParams(window.location.search);
  const filterFields = deserializeFilters(window.location.search);
  if (filterFields) {
   const gridItems = filterFieldsToGridItems(filterFields);
   if (gridItems.length > 0) {
    setFilter({ items: gridItems });
    setInitialState((prev) => ({
     ...prev,
     filter: { filterModel: { items: gridItems } },
    }));
   }
   const lsPreset = getConfigStorageItem(table)?.columns;
   const basePreset: ColPresetType | null =
    lsPreset || presetsColumns?.[pageName]?.[0] || null;
   if (basePreset) {
    const { merged } = buildMergedPreset(basePreset, filterFields);
    dispatch(
     changeCurrentPreset({
      page: pageName,
      type: 'columns',
      preset: merged,
      temp: false,
     }),
    );
    dispatch(
     changeCurrentPreset({
      page: pageName,
      type: 'columns',
      preset: merged,
      temp: true,
     }),
    );
    setConfigStorageItem({ page: pageName, key: 'columns', value: merged });
   }
  }

  setIsInit(true);

  const urlId = urlParams.get('id');
  if (urlId && urlId !== 'undefined') {
   const normalizedId = Number.isNaN(Number(urlId))
    ? urlId
    : Number(urlId);
   setCurrentId(normalizedId);
  }

  const unsubscribe = apiRef.current.subscribeEvent(
   'rowClick',
   (params: GridRowParams, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
    event.defaultMuiPrevented = true;
    if (isSelectingRef.current) {
     return true;
    }

    if (params.row?.Id && !isSelectingRef.current) {
     setCurrentId(params.row.Id);
    }
    return true;
   },
  );

  document.addEventListener('mousedown', handleMouseDown);
  document.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('beforeunload', saveSnapshot);
  return () => {
   unsubscribe();
   document.removeEventListener('mousedown', handleMouseDown);
   document.removeEventListener('mousemove', handleMouseMove);
   window.removeEventListener('beforeunload', saveSnapshot);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 useEffect(() => {
  if (!isInit || !localStorage) return;

  persistTableGridState(pageName, {
   sortModel,
   filter,
   paginationModel,
  });
 }, [isInit, sortModel, filter, paginationModel, pageName]);

 useEffect(() => {
  if (!isInit || sortModel.length === 0 || columnsState.length === 0) return;
  if (hadColumnsRef.current) return;

  hadColumnsRef.current = true;
  const columnFields = new Set(columnsState.map((col) => col.field));
  const applicableSort = sortModel.filter((item) => columnFields.has(item.field));
  if (applicableSort.length === 0) return;

  setSortModel(applicableSort.map((item) => ({ ...item })));
 }, [isInit, columnsState, sortModel]);

 useEffect(() => {
  const filterFields = deserializeFilters(window.location.search);
  if (!filterFields || !hasActiveFilterFields(filterFields)) return;
  if (!presetsColumns?.[pageName]?.[0]) return;

  const basePreset: ColPresetType =
   getConfigStorageItem(table)?.columns || presetsColumns[pageName][0];

  const { merged, hasMissingColumns } = buildMergedPreset(
   basePreset,
   filterFields,
  );

  if (
   !hasMissingColumns &&
   areFilterFieldsEqual(filterFields, basePreset?.filters)
  ) {
   return;
  }

  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset: merged,
    temp: false,
   }),
  );
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset: merged,
    temp: true,
   }),
  );
  setConfigStorageItem({ page: pageName, key: 'columns', value: merged });

  const gridItems = filterFieldsToGridItems(filterFields);
  if (gridItems.length > 0) {
   setFilter({ items: gridItems });
  }
 }, [presetsColumns, pageName, table, dispatch]);

 useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);

  if (currentId) {
   urlParams.set('id', String(currentId));
  } else if (isInit) {
   urlParams.delete('id');
  }

  const newSearch = urlParams.toString();
  const newUrl = newSearch
   ? `${window.location.pathname}?${newSearch}`
   : window.location.pathname;

  window.history.pushState({}, '', newUrl);
 }, [currentId, isInit]);

 // Single owner of preset → URL reflection: keep the URL in sync with the
 // active preset filters so links stay shareable. Guarded by `isInit` and by
 // "has active filters" so it can never clear URL params before the init
 // effects above have read them — clearing the URL is owned by the explicit
 // apply/reset/preset handlers, not here.
 useEffect(() => {
  if (!isInit) return;
  const filters = currentColumnsPreset?.filters ?? null;
  if (!hasActiveFilterFields(filters)) return;
  updateUrlFilterParams(filters);
 }, [isInit, currentColumnsPreset]);

 return {
  isInit,
  filter,
  setFilter,
  sortModel,
  setSortModel,
  paginationModel,
  setPaginationModel,
  initialState,
  loading,
 };
};

export default useTableInit;

